import React from 'react';
import { useI18n } from '@infrastructure/i18n';
import styled from 'styled-components';
import themeGet from '@styled-system/theme-get';
import { A, Button, Flex, Lightbox, Text } from '@ui/wrapper';
import { Show } from '@ui/core/rendering';
import { SIZES } from '@infrastructure/styling/mediaQueries';
import { useOnlyfyOnboardingStore } from '@infrastructure/store';
import { useLanguage } from '@application/customer';
import { LOCALE_DE } from '@config/app.constants';
import {
  ONLYFY_URL_DE,
  ONLYFY_URL_EN,
} from '@infrastructure/routing/constants';

import OnlyfyOnboardingIcon from '@ui/core/assets/onlyfy/xts-onlyfy-onboarding-popup.svg';

const Container = styled(Flex)`
  flex-direction: column;
  align-items: center;
  @media ${SIZES.large} {
    flex-direction: row;
    align-items: start;
  }
`;

const DescriptionWrapper = styled(Flex)`
  flex-direction: column;
  flex: 1;
`;

const Header = styled.h3`
  margin-bottom: ${themeGet('space.5')};
`;

const Body = styled(Lightbox.Body)`
  padding: ${themeGet('space.8')} ${themeGet('space.8')} ${themeGet('space.6')}
    ${themeGet('space.8')};
  @media ${SIZES.large} {
    padding-left: ${themeGet('space.4')};
  }
`;

const Footer = styled(Lightbox.Footer)`
  padding: ${themeGet('space.8')};
  padding-top: 0;
`;

const StrongText = styled.strong`
  margin-top: ${themeGet('space.4')};
`;

const IconWrapper = styled(Flex)`
  margin-right: ${themeGet('space.5')};
  margin-bottom: ${themeGet('space.4')};
  width: 320px;
`;

const Overlay = styled(Flex).attrs({
  alignItems: 'center',
  justifyContent: 'center',
})`
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  z-index: ${themeGet('zIndices.tooltipLevel')};
`;

const i18ns = {
  title1: 'XTS_ONLYFY_POPUP_TITLE1',
  description1: 'XTS_ONLYFY_POPUP_TEXT1',
  title2: 'XTS_ONLYFY_POPUP_TITLE2',
  description2: 'XTS_ONLYFY_POPUP_TEXT2',
  title3: 'XTS_ONLYFY_POPUP_TITLE3',
  description3: 'XTS_ONLYFY_POPUP_TEXT3',
  link: 'XTS_ONLYFY_POPUP_LINK',
  cta: 'XTS_ONLYFY_POPUP_CTA',
} as const;

const onlyfyURL = (locale) =>
  locale === LOCALE_DE ? ONLYFY_URL_DE : ONLYFY_URL_EN;

const OnlyfyOnboardingDialog = () => {
  const { i18n } = useI18n<typeof i18ns>();
  const locale = useLanguage();
  const [seenOnlyfyOnboarding, setSeenOnlyfyOnboarding] =
    useOnlyfyOnboardingStore();
  const closeLightbox = () => setSeenOnlyfyOnboarding(true);

  return (
    // when={false} is a placeholder as we don't want to show the modal.
    <Show when={false} fallback={null}>
      <Overlay data-testid="onlyfy-onboarding-overlay">
        <Lightbox onClose={closeLightbox}>
          <Body>
            <Container>
              <IconWrapper>
                <img src={OnlyfyOnboardingIcon} />
              </IconWrapper>
              <DescriptionWrapper>
                <Header>{i18n(i18ns.title1)}</Header>
                <Text>
                  {i18n(i18ns.description1, {
                    link: (
                      <A href={onlyfyURL(locale)} target="_blank" key="link">
                        {i18n(i18ns.link)}
                      </A>
                    ),
                  })}
                </Text>
                <StrongText>{i18n(i18ns.title2)}</StrongText>
                <Text>{i18n(i18ns.description2)}</Text>
                <StrongText>{i18n(i18ns.title3)}</StrongText>
                <Text>{i18n(i18ns.description3)}</Text>
              </DescriptionWrapper>
            </Container>
          </Body>
          <Footer>
            <Button data-testid="cta-button" onClick={closeLightbox}>
              {i18n(i18ns.cta)}
            </Button>
          </Footer>
        </Lightbox>
      </Overlay>
    </Show>
  );
};

export { OnlyfyOnboardingDialog };
